import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { 
  Home, 
  Receipt, 
  User, 
  LogOut, 
  Building2,
  Calendar,
  Download,
  FileText,
  ChevronRight,
  Menu,
  X,
  AlertTriangle,
  MessageSquare,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  Smartphone,
  CreditCard,
  DollarSign,
  Wallet,
  Phone,
  Loader2,
  ShieldCheck,
  RotateCcw,
  PenLine,
  Wrench,
  Zap,
  Paintbrush,
  KeyRound,
  ClipboardCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { TenantProvider } from '../components/tenant/TenantContext';
import WelcomeOverlay from '../components/WelcomeOverlay';
import TenantOverview from '../components/tenant/TenantOverview';
import TenantPayment from '../components/tenant/TenantPayment';
import TenantPayNow from '../components/tenant/TenantPayNow';
import TenantReceipts from '../components/tenant/TenantReceipts';
import DashboardHero from '../components/DashboardHero';
import TenantCockpit from '../components/TenantCockpit';
import LeaseDurationCounter from '../components/LeaseDurationCounter';
import TenantDeposits from '../components/tenant/TenantDeposits';
import TenantNotices from '../components/tenant/TenantNotices';
import TenantRequests from '../components/tenant/TenantRequests';
import TenantContract from '../components/tenant/TenantContract';
import TenantAnomalies from '../components/tenant/TenantAnomalies';
import { TenantKeyHandovers, TenantInspections, TenantRepairs } from '../components/tenant/TenantDocuments';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { tenantAPI, agencySettingsAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { usePWAInstall, IOSInstallInstructions } from '../lib/pwa';
import { SignaturePad } from '../components/SignaturePad';
import { PortalNewsTicker } from '../components/DashboardNewsTicker';

const paymentMethodLabels = {
  especes: 'Espèces',
  virement: 'Virement',
  mobile_money: 'Mobile Money',
  cheque: 'Chèque'
};

const requestTypeLabels = {
  maintenance: 'Maintenance',
  reclamation: 'Réclamation',
  information: 'Information',
  autre: 'Autre'
};

const priorityLabels = {
  urgente: { label: 'Urgente', color: 'bg-red-100 text-red-700' },
  normale: { label: 'Normale', color: 'bg-blue-100 text-blue-700' },
  basse: { label: 'Basse', color: 'bg-gray-100 text-gray-700' }
};

const statusLabels = {
  en_attente: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  en_cours: { label: 'En cours', color: 'bg-blue-100 text-blue-700', icon: Clock },
  resolu: { label: 'Résolu', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  refuse: { label: 'Refusé', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const dueNoticeStatusLabels = {
  en_attente: { label: 'À payer', color: 'bg-yellow-100 text-yellow-700' },
  paye: { label: 'Payé', color: 'bg-green-100 text-green-700' },
  en_retard: { label: 'En retard', color: 'bg-red-100 text-red-700' }
};

const TenantDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [dueNotices, setDueNotices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [securityDeposits, setSecurityDeposits] = useState([]);
  const [agencySettings, setAgencySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchParams] = useSearchParams();
  const { key: locationKey } = useLocation();
  useEffect(() => {
    const t = searchParams.get('tab');
    if (!t) return;
    setActiveTab(t);
    setTimeout(() => document.querySelector('[data-testid="tenant-tabs"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationKey]);
  // Pay-Now modal (unifié Stripe/CinetPay/Mobile Money)
  const [payNowOpen, setPayNowOpen] = useState(false);
  const [payNoticeId, setPayNoticeId] = useState(null);
  
  // Request form state
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestForm, setRequestForm] = useState({
    request_type: 'plomberie',
    subject: '',
    description: '',
    priority: 'normale'
  });
  const [requestPhotos, setRequestPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [showSignModal, setShowSignModal] = useState(false);
  const [signDocType, setSignDocType] = useState(null);
  const [signDocId, setSignDocId] = useState(null);
  const [signDocLabel, setSignDocLabel] = useState('');
  const [tenantSignatures, setTenantSignatures] = useState({});
  const [myAnomalies, setMyAnomalies] = useState([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [anomalyForm, setAnomalyForm] = useState({
    location_type: 'partie_commune', category: 'plomberie', description: '', priority: 'normale'
  });
  
  // Orange Money Payment state
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [paymentStep, setPaymentStep] = useState('form'); // form, waiting, success, failed
  const [paymentForm, setPaymentForm] = useState({
    phone_number: '',
    amount: '',
    period_month: ''
  });
  const [paymentTransaction, setPaymentTransaction] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [jemeniConfig, setJemeniConfig] = useState(null);
  const pollTimerRef = useRef(null);
  
  // PWA Install
  const { isInstallable, isInstalled, installApp } = usePWAInstall();
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('tenant_token');
    if (!token) {
      navigate('/tenant/login');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [profileRes, receiptsRes, dueNoticesRes, requestsRes, depositsRes, agencyRes] = await Promise.all([
        tenantAPI.getProfile(),
        tenantAPI.getReceipts(),
        tenantAPI.getDueNotices().catch(() => ({ data: [] })),
        tenantAPI.getRequests().catch(() => ({ data: [] })),
        tenantAPI.getSecurityDeposits().catch(() => ({ data: [] })),
        agencySettingsAPI.getForTenant().catch(() => ({ data: null }))
      ]);
      setProfile(profileRes.data);
      setReceipts(receiptsRes.data);
      setDueNotices(dueNoticesRes.data || []);
      setRequests(requestsRes.data || []);
      setSecurityDeposits(depositsRes.data || []);
      setAgencySettings(agencyRes.data);
      
      // Fetch signatures for deposits
      const sigMap = {};
      for (const dep of (depositsRes.data || [])) {
        try {
          const sigRes = await tenantAPI.getSignatures('security_deposit', dep.id);
          if (sigRes.data?.length > 0) sigMap[`security_deposit_${dep.id}`] = sigRes.data;
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('tenant signatures fetch failed', dep.id, err);
        }
      }
      setTenantSignatures(sigMap);
      
      // Fetch anomalies
      try {
        const anomRes = await tenantAPI.getMyAnomalies();
        setMyAnomalies(anomRes.data || []);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.error('anomalies fetch failed', err);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('tenant_token');
        localStorage.removeItem('tenant_data');
        navigate('/tenant/login');
      } else {
        toast.error('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('tenant_token');
    localStorage.removeItem('tenant_data');
    navigate('/tenant/login');
  };

  // ========== Jemenipay Mobile Money Payment Functions ==========

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const openPaymentDialog = async () => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    setPaymentForm({
      phone_number: profile?.tenant?.phone || '',
      amount: profile?.property?.monthly_rent || '',
      period_month: currentMonth
    });
    setPaymentStep('form');
    setPaymentTransaction(null);
    setOtpCode('');
    setIsPaymentDialogOpen(true);
    // Load Jemeni config (to know sandbox vs live)
    try {
      const cfg = await tenantAPI.getJemeniConfig();
      setJemeniConfig(cfg.data);
    } catch (e) {
      setJemeniConfig({ enabled: false });
    }
  };

  const handleInitiatePayment = async () => {
    if (!paymentForm.phone_number || !paymentForm.amount || !paymentForm.period_month) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setProcessingPayment(true);
    try {
      const response = await tenantAPI.initiateJemeniPayment({
        phone_number: paymentForm.phone_number,
        amount: parseFloat(paymentForm.amount),
        period_month: paymentForm.period_month
      });

      setPaymentTransaction(response.data);
      setPaymentStep('waiting');
      toast.success('Demande envoyee. Confirmez sur votre telephone.');

      // Start polling status every 3 seconds, up to 3 minutes
      stopPolling();
      const startedAt = Date.now();
      pollTimerRef.current = setInterval(async () => {
        if (Date.now() - startedAt > 180000) { // 3 min timeout
          stopPolling();
          setPaymentStep('failed');
          toast.error('Delai de confirmation depasse');
          return;
        }
        try {
          const r = await tenantAPI.getJemeniStatus(response.data.transaction_id);
          if (r.data.status === 'completed') {
            stopPolling();
            setPaymentStep('success');
            toast.success('Paiement reussi !');
            fetchData();
          } else if (r.data.status === 'failed') {
            stopPolling();
            setPaymentStep('failed');
            toast.error('Paiement echoue ou annule');
          }
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('payment polling transient error', err);
        }
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'initiation du paiement');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleConfirmPayment = async () => {
    // Manual refresh check (user clicks "J'ai deja confirme")
    if (!paymentTransaction) return;
    setProcessingPayment(true);
    try {
      const r = await tenantAPI.getJemeniStatus(paymentTransaction.transaction_id);
      if (r.data.status === 'completed') {
        stopPolling();
        setPaymentStep('success');
        toast.success('Paiement confirme !');
        fetchData();
      } else if (r.data.status === 'failed') {
        stopPolling();
        setPaymentStep('failed');
        toast.error('Paiement echoue');
      } else {
        toast.info('Paiement toujours en attente. Veuillez valider sur votre telephone.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de verification');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleCancelPayment = async () => {
    stopPolling();
    setIsPaymentDialogOpen(false);
    setPaymentStep('form');
    setPaymentTransaction(null);
    setOtpCode('');
  };

  useEffect(() => {
    return () => stopPolling();
  }, []);

  const getMonthOptions = () => {
    const options = [];
    const today = new Date();
    for (let i = 0; i < 3; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const value = date.toISOString().slice(0, 7);
      const label = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      options.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return options;
  };

  // ========== End Orange Money Functions ==========

  const handleViewReceipt = async (receipt) => {
    try {
      const response = await tenantAPI.getReceipt(receipt.id);
      setSelectedReceipt(response.data);
      setIsReceiptOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement de la quittance');
    }
  };

  const handleDownloadReceiptPdf = async (receiptId, receiptNumber) => {
    setDownloading(receiptId);
    try {
      const response = await tenantAPI.downloadReceiptPdf(receiptId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `quittance_${receiptNumber || receiptId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Quittance téléchargée avec succès');
    } catch (error) {
      toast.error('Erreur lors du téléchargement de la quittance');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadDueNoticePdf = async (noticeId, noticeNumber) => {
    setDownloading(`notice-${noticeId}`);
    try {
      const response = await tenantAPI.downloadDueNoticePdf(noticeId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `avis_echeance_${noticeNumber || noticeId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Avis d\'échéance téléchargé avec succès');
    } catch (error) {
      toast.error('Erreur lors du téléchargement de l\'avis');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadDepositPdf = async (depositId, reference) => {
    setDownloading(`deposit-${depositId}`);
    try {
      const response = await tenantAPI.downloadSecurityDepositPdf(depositId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attestation_depot_${reference || depositId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Attestation de dépôt téléchargée avec succès');
    } catch (error) {
      toast.error('Erreur lors du téléchargement de l\'attestation');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadLeaseContract = async () => {
    setDownloading('lease-contract');
    try {
      const response = await tenantAPI.downloadLeaseContract();
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contrat_location_${profile?.client_code || 'mon_contrat'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Contrat de location téléchargé avec succès');
    } catch (error) {
      console.error('Error downloading lease contract:', error);
      toast.error('Erreur lors du téléchargement du contrat');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadResidenceCertificate = async () => {
    setDownloading('residence-certificate');
    try {
      const response = await tenantAPI.downloadResidenceCertificate();
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attestation_domicile_${profile?.tenant?.client_code || 'attestation'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Attestation de domicile téléchargée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setDownloading(null);
    }
  };

  const openSignModal = (docType, docId, label) => {
    setSignDocType(docType);
    setSignDocId(docId);
    setSignDocLabel(label);
    setShowSignModal(true);
  };

  const handleTenantSign = async (signatureData) => {
    if (!signDocType || !signDocId) return;
    try {
      await tenantAPI.saveSignature({
        document_type: signDocType,
        document_id: signDocId,
        signature_data: signatureData
      });
      toast.success('Document signé avec succès !');
      setShowSignModal(false);
      setSignDocType(null);
      setSignDocId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    }
  };

  const getTenantSignatureStatus = (docType, docId) => {
    const key = `${docType}_${docId}`;
    const sigs = tenantSignatures[key] || [];
    const hasTenant = sigs.some(s => s.signer_type === 'tenant');
    const hasAdmin = sigs.some(s => s.signer_type === 'admin' || s.signer_type === 'agent');
    return { hasTenant, hasAdmin, count: sigs.length };
  };

  const handleReportAnomaly = async (e) => {
    e.preventDefault();
    if (!anomalyForm.description || !anomalyForm.category) {
      toast.error('Veuillez remplir la description et la catégorie');
      return;
    }
    try {
      await tenantAPI.reportAnomaly(anomalyForm);
      toast.success('Anomalie signalée avec succès !');
      setShowReportModal(false);
      setAnomalyForm({ location_type: 'partie_commune', category: 'plomberie', description: '', priority: 'normale' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du signalement');
    }
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  const handleCreateRequest = async () => {
    if (!requestForm.subject.trim() || !requestForm.description.trim()) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setSubmitting(true);
    try {
      // Upload photos first
      const photoUrls = [];
      for (const f of requestPhotos) {
        try {
          const fd = new FormData();
          fd.append('file', f);
          const r = await tenantAPI.uploadRequestPhoto(fd);
          if (r.data?.url) photoUrls.push(r.data.url);
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('photo upload failed', err);
        }
      }
      await tenantAPI.createRequest({ ...requestForm, photos: photoUrls });
      toast.success('Demande envoyée avec succès');
      setIsRequestDialogOpen(false);
      setRequestForm({
        request_type: 'plomberie',
        subject: '',
        description: '',
        priority: 'normale'
      });
      setRequestPhotos([]);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de la demande');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="spinner" />
      </div>
    );
  }

  const pendingDueNotices = dueNotices.filter(n => n.status === 'en_attente' || n.status === 'en_retard');
  const pendingRequests = requests.filter(r => r.status === 'en_attente' || r.status === 'en_cours');

  return (
    <div className="min-h-screen bg-hero-gradient">
      <WelcomeOverlay
        spaceType="tenant"
        userId={profile?.tenant?.id}
        userName={profile?.tenant?.name}
        agencyName={agencySettings?.name}
        agencyLogoUrl={agencySettings?.logo_url}
        agencySlogan={agencySettings?.slogan}
      />
      <PortalNewsTicker portal="tenant" />
      {/* Header brand gradient */}
      <header className="bg-brand-gradient border-b border-[#D4A745]/20 sticky top-0 z-20 shadow-md">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {agencySettings?.logo_url ? (
                <div className="w-11 h-11 rounded-xl bg-white/95 p-1 shadow-md ring-1 ring-white/20">
                  <img
                    src={agencySettings.logo_url.startsWith('http') ? agencySettings.logo_url : `${process.env.REACT_APP_BACKEND_URL}${agencySettings.logo_url}`}
                    alt={agencySettings?.name || "Logo Agence"}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      if (e.target.parentElement?.nextSibling) e.target.parentElement.nextSibling.style.display = 'flex';
                    }}
                  />
                </div>
              ) : null}
              <div
                className={`w-11 h-11 rounded-xl bg-gold-gradient items-center justify-center shadow-md ${agencySettings?.logo_url ? 'hidden' : 'flex'}`}
              >
                <Home className="w-5 h-5 text-[#1B3A4B]" />
              </div>
              <div>
                <h1 className="font-bold text-white tracking-tight">Espace Locataire</h1>
                <p className="text-[11px] text-[#E8C56F] font-medium">{agencySettings?.name || "GAB'S SERVICES"}</p>
              </div>
            </div>

            {/* Desktop menu */}
            <div className="hidden sm:flex items-center gap-4">
              <span className="text-sm text-white/90">
                {profile?.tenant?.name}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-white/90 hover:text-white hover:bg-white/10"
                data-testid="tenant-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="sm:hidden p-2 hover:bg-white/10 rounded-lg text-white"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="sm:hidden mt-4 pt-4 border-t border-white/10">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white/90">{profile?.tenant?.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="text-[#E8C56F] hover:bg-white/10"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <TenantProvider value={{
        profile, receipts, dueNotices, requests, securityDeposits, agencySettings,
        downloading, selectedReceipt, isReceiptOpen, setIsReceiptOpen,
        handleViewReceipt, handleDownloadReceiptPdf, handleDownloadDueNoticePdf,
        handleDownloadDepositPdf, handleDownloadLeaseContract, handleDownloadResidenceCertificate, formatCurrency, formatDate,
        paymentMethodLabels, requestTypeLabels, priorityLabels, statusLabels, dueNoticeStatusLabels,
        setIsRequestDialogOpen, openPaymentDialog, myAnomalies, setShowReportModal,
        tenantSignatures, setShowSignModal, setSignDocType, setSignDocId, setSignDocLabel,
        isInstallable, isIOS, isInstalled, installApp, setShowIOSInstructions,
        setActiveTab, openSignModal, handleViewRequest,
        openPayNow: (noticeId = null) => { setPayNoticeId(noticeId); setPayNowOpen(true); },
      }}>
      <main className="max-w-5xl mx-auto px-4 py-6" data-testid="tenant-dashboard">
        {/* === Cockpit === */}
        <TenantCockpit profile={profile} />

        {/* === Hero === */}
        <div className="mb-6">
          <DashboardHero
            accentColor="#10B981"
            role="Locataire"
            greeting={`Bonjour, ${(profile?.tenant?.name || '').split(' ')[0] || 'Bienvenue'}`}
            subtitle={profile?.property?.title
              ? `Vous occupez : ${profile.property.title}${profile.property.address ? ` - ${profile.property.address}` : ''}`
              : 'Tout votre logement, en un seul espace.'}
            metrics={[
              {
                label: 'Loyer mensuel',
                value: profile?.property?.monthly_rent
                  ? formatCurrency(profile.property.monthly_rent, profile?.property?.currency || 'XOF')
                  : '—',
                icon: Wallet,
                accent: true,
              },
              {
                label: 'Avis a payer',
                value: dueNotices.filter(d => d.status !== 'paye').length,
                icon: Receipt,
              },
              {
                label: 'Quittances',
                value: receipts.length,
                icon: Receipt,
              },
              {
                label: 'Demandes en cours',
                value: requests.filter(r => r.status !== 'completed' && r.status !== 'rejected').length,
                icon: Wrench,
              },
            ]}
          />
        </div>

        {/* === Bouton global Payer mon loyer === */}
        {profile?.stats?.unpaid_amount > 0 && (
          <div className="mb-6">
            <button
              type="button"
              onClick={() => { setPayNoticeId(null); setPayNowOpen(true); }}
              className="w-full p-5 rounded-2xl bg-gradient-to-r from-[#B85C38] to-[#D4A745] text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-between group"
              data-testid="pay-rent-global-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Wallet className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <div className="font-bold text-lg">Payer mon loyer</div>
                  <div className="text-sm text-white/90">
                    Solde impayé : {new Intl.NumberFormat('fr-FR').format(profile.stats.unpaid_amount)} FCFA · Mobile Money, Carte ou CinetPay
                  </div>
                </div>
              </div>
              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-sm font-semibold">Payer →</span>
              </div>
            </button>
          </div>
        )}

        {/* Lease duration counter */}
        {profile?.lease?.start_date && (
          <div className="mb-6" data-testid="tenant-lease-duration">
            <LeaseDurationCounter
              startDate={profile.lease.start_date}
              endDate={profile.lease.status === 'termine' ? profile.lease.end_date : null}
              accentColor={profile.lease.status === 'termine' ? 'rose' : 'emerald'}
              label="Ancienneté de votre bail"
            />
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6" data-testid="tenant-tabs">
          <TabsList className="flex flex-wrap h-auto gap-1.5 p-1.5 bg-white border border-gray-200 shadow-sm rounded-xl w-full lg:w-auto">
            <TabsTrigger value="overview" className="flex items-center gap-2 data-[state=active]:bg-brand-gradient data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg transition-all">
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Apercu</span>
            </TabsTrigger>
            <TabsTrigger value="payment" className="flex items-center gap-2 data-[state=active]:bg-brand-gradient data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg transition-all">
              <Wallet className="w-4 h-4" />
              <span className="hidden sm:inline">Payer</span>
            </TabsTrigger>
            <TabsTrigger value="receipts" className="flex items-center gap-2 data-[state=active]:bg-brand-gradient data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg transition-all">
              <Receipt className="w-4 h-4" />
              <span className="hidden sm:inline">Quittances</span>
            </TabsTrigger>
            <TabsTrigger value="deposits" className="flex items-center gap-2 data-[state=active]:bg-brand-gradient data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg transition-all">
              <ShieldCheck className="w-4 h-4" />
              <span className="hidden sm:inline">Caution</span>
            </TabsTrigger>
            <TabsTrigger value="notices" className="flex items-center gap-2 relative data-[state=active]:bg-brand-gradient data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg transition-all">
              <AlertTriangle className="w-4 h-4" />
              <span className="hidden sm:inline">Avis</span>
              {pendingDueNotices.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {pendingDueNotices.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="requests" className="flex items-center gap-2 relative data-[state=active]:bg-brand-gradient data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg transition-all">
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">Demandes</span>
              {pendingRequests.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#F2A900] text-[#1B3A4B] text-xs rounded-full flex items-center justify-center font-bold">
                  {pendingRequests.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="contract" className="flex items-center gap-2 data-[state=active]:bg-brand-gradient data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg transition-all">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Contrat</span>
            </TabsTrigger>
            <TabsTrigger value="anomalies" className="flex items-center gap-2 data-[state=active]:bg-brand-gradient data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg transition-all">
              <AlertTriangle className="w-4 h-4" />
              <span className="hidden sm:inline">Anomalies</span>
            </TabsTrigger>
            <TabsTrigger value="keys" className="flex items-center gap-2 data-[state=active]:bg-brand-gradient data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg transition-all" data-testid="tab-keys">
              <KeyRound className="w-4 h-4" />
              <span className="hidden sm:inline">Remises</span>
            </TabsTrigger>
            <TabsTrigger value="inspections" className="flex items-center gap-2 data-[state=active]:bg-brand-gradient data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg transition-all" data-testid="tab-inspections">
              <ClipboardCheck className="w-4 h-4" />
              <span className="hidden sm:inline">Etat des lieux</span>
            </TabsTrigger>
            <TabsTrigger value="repairs" className="flex items-center gap-2 data-[state=active]:bg-brand-gradient data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg transition-all" data-testid="tab-repairs">
              <Wrench className="w-4 h-4" />
              <span className="hidden sm:inline">Reparations</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <TenantOverview />
          </TabsContent>

          {/* Payment Tab */}
          <TabsContent value="payment" className="space-y-6">
            <TenantPayment />
          </TabsContent>

          {/* Receipts Tab */}
          <TabsContent value="receipts" className="space-y-4">
            <TenantReceipts />
          </TabsContent>

          {/* Deposits Tab */}
          <TabsContent value="deposits" className="space-y-4">
            <TenantDeposits />
          </TabsContent>

          {/* Notices Tab */}
          <TabsContent value="notices" className="space-y-4">
            <TenantNotices />
          </TabsContent>

          {/* Requests Tab */}
          <TabsContent value="requests" className="space-y-4">
            <TenantRequests />
          </TabsContent>

          {/* Contract Tab */}
          <TabsContent value="contract" className="space-y-4">
            <TenantContract />
          </TabsContent>

          {/* Anomalies Tab */}
          <TabsContent value="anomalies" className="space-y-4">
            <TenantAnomalies />
          </TabsContent>

          {/* Key Handovers Tab */}
          <TabsContent value="keys" className="space-y-4">
            <TenantKeyHandovers />
          </TabsContent>

          {/* Property Inspections Tab */}
          <TabsContent value="inspections" className="space-y-4">
            <TenantInspections />
          </TabsContent>

          {/* Repairs Tab */}
          <TabsContent value="repairs" className="space-y-4">
            <TenantRepairs />
          </TabsContent>
        </Tabs>
      </main>
      </TenantProvider>

      {/* Pay-Now modal (Stripe / CinetPay / Mobile Money manuel) */}
      <TenantPayNow
        open={payNowOpen}
        onClose={() => setPayNowOpen(false)}
        dueNotices={dueNotices}
        initialNoticeId={payNoticeId}
        onPaid={() => { fetchData(); }}
      />

      {/* Receipt Modal */}
      <Dialog open={isReceiptOpen} onOpenChange={setIsReceiptOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Quittance de Loyer</DialogTitle>
          </DialogHeader>
          
          {selectedReceipt && (
            <div className="print:p-8" id="receipt-content">
              <div className="border border-gray-200 rounded-lg p-6 bg-white">
                {/* Header with Agency Info */}
                <div className="flex justify-between items-start mb-6 pb-4 border-b">
                  <div className="flex items-center gap-4">
                    {selectedReceipt.agency?.logo_url ? (
                      <img 
                        src={selectedReceipt.agency.logo_url} 
                        alt="Logo" 
                        className="h-14 object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-[#1B3A4B] flex items-center justify-center">
                        <Building2 className="w-7 h-7 text-white" />
                      </div>
                    )}
                    <div>
                      <h2 className="text-2xl font-bold text-[#1B3A4B]">
                        {selectedReceipt.agency?.name || "GAB'S SERVICES HABITAT"}
                      </h2>
                      <p className="text-sm text-gray-500">
                        {selectedReceipt.agency?.slogan || 'Gestion Immobilière'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right text-sm text-gray-600">
                    {selectedReceipt.agency?.address && <p>{selectedReceipt.agency.address}</p>}
                    <p>{selectedReceipt.agency?.city || 'Bamako'}, {selectedReceipt.agency?.country || 'Mali'}</p>
                    {selectedReceipt.agency?.phone && <p>Tél: {selectedReceipt.agency.phone}</p>}
                    {selectedReceipt.agency?.email && <p>{selectedReceipt.agency.email}</p>}
                    {selectedReceipt.agency?.tax_id && (
                      <p className="text-xs mt-1">NIF: {selectedReceipt.agency.tax_id}</p>
                    )}
                  </div>
                </div>

                {/* Title */}
                <div className="text-center mb-6">
                  <h3 className="text-xl font-semibold text-gray-900">QUITTANCE DE LOYER</h3>
                  <p className="text-sm text-gray-500">N° {selectedReceipt.receipt_number}</p>
                </div>
                
                {/* Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Locataire</h3>
                    <p className="font-semibold">{selectedReceipt.tenant_name}</p>
                    {selectedReceipt.tenant_code && (
                      <p className="text-xs font-mono text-[#1B3A4B]">{selectedReceipt.tenant_code}</p>
                    )}
                    <p className="text-sm text-gray-600">{selectedReceipt.tenant_phone}</p>
                    {selectedReceipt.tenant_email && (
                      <p className="text-sm text-gray-600">{selectedReceipt.tenant_email}</p>
                    )}
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Bien loué</h3>
                    <p className="font-semibold">{selectedReceipt.property_title}</p>
                    {selectedReceipt.property_code && (
                      <p className="text-xs font-mono text-[#1B3A4B]">{selectedReceipt.property_code}</p>
                    )}
                    <p className="text-sm text-gray-600">{selectedReceipt.property_address}</p>
                  </div>
                </div>
                
                {/* Payment Info */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Période couverte</p>
                      <p className="font-medium">
                        Du {formatDate(selectedReceipt.period_start)} au {formatDate(selectedReceipt.period_end)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Date de paiement</p>
                      <p className="font-medium">{formatDate(selectedReceipt.payment_date)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Mode de paiement</p>
                      <p className="font-medium">{paymentMethodLabels[selectedReceipt.payment_method]}</p>
                    </div>
                    {selectedReceipt.reference && (
                      <div>
                        <p className="text-sm text-gray-500">Référence</p>
                        <p className="font-medium">{selectedReceipt.reference}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Amount */}
                <div className="text-center py-6 border-t border-b mb-6">
                  <p className="text-sm text-gray-500 mb-1">Montant reçu</p>
                  <p className="text-4xl font-bold text-[#1B3A4B]">
                    {formatCurrency(selectedReceipt.amount, selectedReceipt.currency)}
                  </p>
                </div>
                
                {/* Legal text */}
                <div className="text-sm text-gray-600 mb-6">
                  <p>
                    Je soussigné(e), représentant l'agence <strong>{selectedReceipt.agency?.name || "GAB'S SERVICES HABITAT"}</strong>, 
                    reconnais avoir reçu de <strong>{selectedReceipt.tenant_name}</strong> la somme 
                    de <strong>{formatCurrency(selectedReceipt.amount, selectedReceipt.currency)}</strong> au 
                    titre du loyer pour la période du {formatDate(selectedReceipt.period_start)} au {formatDate(selectedReceipt.period_end)}.
                  </p>
                </div>

                {/* Bank Info */}
                {(selectedReceipt.agency?.bank_name || selectedReceipt.agency?.bank_account) && (
                  <div className="bg-blue-50 rounded-lg p-4 mb-6 text-sm">
                    <p className="font-medium text-blue-800 mb-1">Informations bancaires:</p>
                    {selectedReceipt.agency.bank_name && (
                      <p className="text-blue-700">Banque: {selectedReceipt.agency.bank_name}</p>
                    )}
                    {selectedReceipt.agency.bank_account && (
                      <p className="text-blue-700">Compte: {selectedReceipt.agency.bank_account}</p>
                    )}
                  </div>
                )}
                
                {/* Footer */}
                <div className="text-center text-xs text-gray-500">
                  <p>Cette quittance ne libère le locataire que pour la période indiquée.</p>
                  <p className="mt-2">Document émis le {formatDate(selectedReceipt.created_at)}</p>
                  {selectedReceipt.agency?.website && (
                    <p className="mt-1">{selectedReceipt.agency.website}</p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setIsReceiptOpen(false)}>
              Fermer
            </Button>
            <Button
              variant="outline"
              onClick={() => handleDownloadReceiptPdf(selectedReceipt?.id, selectedReceipt?.receipt_number)}
              disabled={downloading === selectedReceipt?.id}
            >
              <Download className="w-4 h-4 mr-2" />
              Télécharger PDF
            </Button>
            <Button 
              onClick={handlePrintReceipt}
              className="bg-[#1B3A4B] hover:bg-[#142D3A]"
              data-testid="tenant-print-receipt-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Imprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Request Dialog */}
      <Dialog open={isRequestDialogOpen} onOpenChange={setIsRequestDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouvelle Demande</DialogTitle>
            <DialogDescription>
              Envoyez une demande à votre agence. Nous vous répondrons dans les plus brefs délais.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="request_type">Type de demande</Label>
                <Select
                  value={requestForm.request_type}
                  onValueChange={(value) => setRequestForm({...requestForm, request_type: value})}
                >
                  <SelectTrigger id="request_type" data-testid="request-type-select">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plomberie">Plomberie</SelectItem>
                    <SelectItem value="electricite">Electricite</SelectItem>
                    <SelectItem value="serrurerie">Serrurerie</SelectItem>
                    <SelectItem value="peinture">Peinture</SelectItem>
                    <SelectItem value="chauffage">Chauffage / Climatisation</SelectItem>
                    <SelectItem value="menuiserie">Menuiserie</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="priority">Urgence</Label>
                <Select
                  value={requestForm.priority}
                  onValueChange={(value) => setRequestForm({...requestForm, priority: value})}
                >
                  <SelectTrigger id="priority" data-testid="request-priority-select">
                    <SelectValue placeholder="Priorité" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgente">Urgente</SelectItem>
                    <SelectItem value="normale">Normale</SelectItem>
                    <SelectItem value="faible">Faible</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="subject">Sujet *</Label>
              <Input
                id="subject"
                placeholder="Objet de votre demande"
                value={requestForm.subject}
                onChange={(e) => setRequestForm({...requestForm, subject: e.target.value})}
                data-testid="request-subject-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                placeholder="Décrivez votre demande en détail..."
                rows={4}
                value={requestForm.description}
                onChange={(e) => setRequestForm({...requestForm, description: e.target.value})}
                data-testid="request-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="photos">Photos (facultatif)</Label>
              <Input
                id="photos"
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => setRequestPhotos(Array.from(e.target.files || []).slice(0, 5))}
                data-testid="request-photos-input"
              />
              {requestPhotos.length > 0 && (
                <p className="text-xs text-emerald-700">{requestPhotos.length} photo(s) jointes</p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRequestDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleCreateRequest}
              disabled={submitting}
              className="bg-[#1B3A4B] hover:bg-[#142D3A]"
              data-testid="submit-request-btn"
            >
              {submitting ? (
                <>
                  <span className="spinner mr-2" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Request Dialog */}
      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Détail de la Demande</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={priorityLabels[selectedRequest.priority]?.color}>
                  {priorityLabels[selectedRequest.priority]?.label}
                </Badge>
                <Badge variant="outline">
                  {requestTypeLabels[selectedRequest.request_type]}
                </Badge>
                <Badge className={statusLabels[selectedRequest.status]?.color}>
                  {statusLabels[selectedRequest.status]?.label}
                </Badge>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Numéro</p>
                <p className="font-mono">{selectedRequest.request_number}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Sujet</p>
                <p className="font-medium">{selectedRequest.subject}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Description</p>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedRequest.description}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Date de création</p>
                <p>{formatDate(selectedRequest.created_at)}</p>
              </div>
              
              {selectedRequest.response && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-800 mb-2">Réponse de l'agence:</p>
                  <p className="text-blue-700 whitespace-pre-wrap">{selectedRequest.response}</p>
                  {selectedRequest.responded_at && (
                    <p className="text-xs text-blue-500 mt-2">
                      Répondu le {formatDate(selectedRequest.responded_at)}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedRequest(null)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iOS Install Instructions */}
      {showIOSInstructions && (
        <IOSInstallInstructions onClose={() => setShowIOSInstructions(false)} />
      )}

      {/* Jemenipay Payment Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={(open) => !open && handleCancelPayment()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-full flex items-center justify-center">
                <Phone className="w-4 h-4 text-white" />
              </div>
              Paiement Mobile Money (Jemenipay)
            </DialogTitle>
          </DialogHeader>
          
          {/* Step 1: Payment Form */}
          {paymentStep === 'form' && (
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg text-center">
                <p className="text-sm text-orange-700">Montant a payer</p>
                <p className="text-3xl font-bold text-orange-600">
                  {formatCurrency(parseFloat(paymentForm.amount) || 0, 'XOF')}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Numero Mobile Money (Orange / Moov) *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="XX XX XX XX (8 chiffres)"
                    value={paymentForm.phone_number}
                    onChange={(e) => setPaymentForm({...paymentForm, phone_number: e.target.value})}
                    className="pl-10"
                    data-testid="payment-phone-input"
                  />
                </div>
                {jemeniConfig?.mode === 'sandbox' && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    Mode Sandbox actif. Numero de test: <strong>98745632</strong>
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Mois de loyer *</Label>
                <Select 
                  value={paymentForm.period_month} 
                  onValueChange={(v) => setPaymentForm({...paymentForm, period_month: v})}
                >
                  <SelectTrigger data-testid="payment-month-select">
                    <SelectValue placeholder="Selectionner le mois" />
                  </SelectTrigger>
                  <SelectContent>
                    {getMonthOptions().map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="amount">Montant (FCFA) *</Label>
                <Input
                  id="amount"
                  type="number"
                  placeholder="150000"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                  data-testid="payment-amount-input"
                />
              </div>
              
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={handleCancelPayment}>
                  Annuler
                </Button>
                <Button 
                  onClick={handleInitiatePayment}
                  disabled={processingPayment}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="initiate-payment-btn"
                >
                  {processingPayment ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi...
                    </>
                  ) : (
                    <>
                      Continuer
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
          
          {/* Step 2: Waiting for Phone Confirmation */}
          {paymentStep === 'waiting' && paymentTransaction && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg text-center border border-blue-200">
                <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-2" />
                <p className="text-sm text-blue-700">Demande envoyee a</p>
                <p className="text-lg font-semibold text-blue-800">{paymentForm.phone_number}</p>
                <p className="text-xs text-blue-600 mt-2">
                  Validez le paiement sur votre telephone (notification Orange Money / Moov Money)
                </p>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-600">
                <p className="flex items-center gap-2"><Clock className="w-3 h-3" /> Reference : <span className="font-mono">{paymentTransaction.transaction_id?.slice(0, 16)}...</span></p>
                <p className="flex items-center gap-2 mt-1"><DollarSign className="w-3 h-3" /> Montant : {formatCurrency(paymentTransaction.amount, 'XOF')}</p>
              </div>

              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={handleCancelPayment}>
                  Annuler
                </Button>
                <Button
                  onClick={handleConfirmPayment}
                  disabled={processingPayment}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="confirm-payment-btn"
                >
                  {processingPayment ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verification...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      J'ai confirme sur mon telephone
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 2b: Failed */}
          {paymentStep === 'failed' && (
            <div className="space-y-4 text-center py-4">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <X className="w-10 h-10 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-red-800">Paiement echoue</h3>
                <p className="text-gray-600 mt-1">
                  La transaction n'a pas pu etre validee. Verifiez votre solde ou reessayez.
                </p>
              </div>
              <Button onClick={handleCancelPayment} variant="outline">Fermer</Button>
            </div>
          )}
          
          {/* Step 3: Success */}
          {paymentStep === 'success' && (
            <div className="space-y-4 text-center py-4">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-green-800">Paiement Reussi!</h3>
                <p className="text-gray-600 mt-1">
                  Votre paiement de {formatCurrency(parseFloat(paymentForm.amount), 'XOF')} a ete effectue.
                </p>
              </div>
              <p className="text-sm text-gray-500">
                Une quittance a ete generee automatiquement et est disponible dans l'onglet "Quittances".
              </p>
              <Button 
                onClick={() => {
                  setIsPaymentDialogOpen(false);
                  setActiveTab('receipts');
                }}
                className="bg-[#1B3A4B] hover:bg-[#142D3A]"
              >
                Voir mes quittances
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Toaster position="top-right" richColors />

      {/* Report Anomaly Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Signaler une Anomalie
            </DialogTitle>
            <DialogDescription>
              Décrivez le problème constaté dans votre logement ou les parties communes
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleReportAnomaly} className="space-y-4">
            <div>
              <Label>Localisation</Label>
              <Select value={anomalyForm.location_type} onValueChange={v => setAnomalyForm(f => ({ ...f, location_type: v }))}>
                <SelectTrigger data-testid="tenant-anomaly-location"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="partie_commune">Partie commune (escalier, couloir, etc.)</SelectItem>
                  <SelectItem value="locataire">Mon logement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select value={anomalyForm.category} onValueChange={v => setAnomalyForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="tenant-anomaly-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="plomberie">Plomberie</SelectItem>
                  <SelectItem value="electricite">Électricité</SelectItem>
                  <SelectItem value="peinture">Peinture</SelectItem>
                  <SelectItem value="serrurerie">Serrurerie</SelectItem>
                  <SelectItem value="menuiserie">Menuiserie</SelectItem>
                  <SelectItem value="humidite">Humidité</SelectItem>
                  <SelectItem value="fissure">Fissure</SelectItem>
                  <SelectItem value="toiture">Toiture</SelectItem>
                  <SelectItem value="nettoyage">Nettoyage</SelectItem>
                  <SelectItem value="securite">Sécurité</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priorité</Label>
              <Select value={anomalyForm.priority} onValueChange={v => setAnomalyForm(f => ({ ...f, priority: v }))}>
                <SelectTrigger data-testid="tenant-anomaly-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="faible">Faible</SelectItem>
                  <SelectItem value="normale">Normale</SelectItem>
                  <SelectItem value="urgente">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea value={anomalyForm.description} onChange={e => setAnomalyForm(f => ({ ...f, description: e.target.value }))} placeholder="Décrivez le problème en détail..." rows={3} data-testid="tenant-anomaly-description" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowReportModal(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="tenant-submit-anomaly">Signaler</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Signature Modal */}
      <Dialog open={showSignModal} onOpenChange={(open) => { setShowSignModal(open); if (!open) { setSignDocType(null); setSignDocId(null); } }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="w-5 h-5 text-[#1B3A4B]" />
              Signer le document
            </DialogTitle>
            <DialogDescription>
              {signDocLabel}
            </DialogDescription>
          </DialogHeader>
          <SignaturePad
            onSave={handleTenantSign}
            onCancel={() => { setShowSignModal(false); setSignDocType(null); setSignDocId(null); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantDashboard;
